import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import PublicBrowseShell from "../components/PublicBrowseShell";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Bank, ArrowSquareOut, Info, Tractor, Storefront, MapPin } from "@phosphor-icons/react";

const audienceLabel = {
  farmer: "Farmers",
  small_business: "Small Business",
  startup: "Startups",
  nonprofit: "Nonprofits",
};

const CapitalAccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // The API filter by category is not exposed; pull a wide slice and filter client-side.
        const res = await axios.get(`${API}/grants?limit=200`);
        const all = res.data.grants || res.data || [];
        const capitalOnly = (Array.isArray(all) ? all : []).filter(
          (g) => g.category === "Capital Access"
        );
        setLoans(capitalOnly);
      } catch (e) {
        setLoans([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const Shell = user ? DashboardLayout : PublicBrowseShell;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto space-y-8 pb-16" data-testid="capital-access-page">
        {/* Hero */}
        <div className="border-l-4 border-amber-500 pl-6 py-2">
          <div className="flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2">
            <Bank weight="duotone" className="w-4 h-4" />
            <span>Capital Access</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-3" data-testid="capital-access-heading">
            Loans, not grants.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-base leading-relaxed">
            Sometimes the right answer isn't a grant &mdash; it's a low-rate loan from a
            mission-driven lender. Every option below is a real capital partner
            serving South Carolina. All must be repaid. All were verified against
            the lender's own primary source before being listed here.
          </p>
        </div>

        {/* Honest framing callout */}
        <Card className="bg-amber-500/5 border-amber-500/30" data-testid="capital-access-callout">
          <CardContent className="p-5 flex items-start gap-4">
            <Info weight="duotone" className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-300 leading-relaxed">
              <span className="font-semibold text-amber-400">Why this page exists.</span>{" "}
              FlashGrants is a grants platform &mdash; but rural entrepreneurs, farmers,
              and nonprofits often need capital that grants can't cover. These are the
              lenders we trust to send our own community to. Semantic honesty first:
              <span className="text-white font-semibold"> these are loans. They must be repaid.</span>
            </div>
          </CardContent>
        </Card>

        {/* Loans grid */}
        {loading ? (
          <p className="text-zinc-500 text-sm" data-testid="capital-access-loading">Loading capital partners...</p>
        ) : loans.length === 0 ? (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-8 text-center text-zinc-500">
              No capital partners loaded yet.
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="capital-access-grid">
            {loans.map((loan) => (
              <Card
                key={loan.grant_id || loan.id}
                className="bg-zinc-950 border-zinc-800 hover:border-amber-500/50 transition"
                data-testid={`capital-loan-card-${loan.grant_id}`}
              >
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-serif text-white leading-snug mb-1">
                        {loan.title?.replace(" (Capital Access)", "")}
                      </h3>
                      <p className="text-xs text-zinc-500 font-medium">{loan.funder_name}</p>
                    </div>
                    {(loan.audience || []).includes("farmer") ? (
                      <Tractor weight="duotone" className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <Storefront weight="duotone" className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    )}
                  </div>

                  <p className="text-sm text-zinc-400 leading-relaxed line-clamp-4">
                    {loan.description}
                  </p>

                  {/* Meta row */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {loan.amount_max ? (
                      <span className="text-xs px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-emerald-400 font-mono">
                        Up to ${loan.amount_max.toLocaleString()}
                      </span>
                    ) : null}
                    {(loan.audience || []).map((a) => (
                      <span
                        key={a}
                        className="text-xs px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-zinc-400"
                      >
                        {audienceLabel[a] || a}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-3">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/grants/${loan.grant_id || loan.id}`)}
                      className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                      data-testid={`capital-loan-details-${loan.grant_id}`}
                    >
                      See details
                    </Button>
                    {loan.application_url ? (
                      <a
                        href={loan.application_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-zinc-400 hover:text-amber-400 flex items-center gap-1"
                        data-testid={`capital-loan-external-${loan.grant_id}`}
                      >
                        Lender site <ArrowSquareOut className="w-3.5 h-3.5" />
                      </a>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer note */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="capital-access-footer">
          <CardContent className="p-5 flex items-start gap-4">
            <MapPin weight="duotone" className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-400 leading-relaxed">
              Not sure if a loan or a grant is right for you?{" "}
              <Link to="/grants" className="text-emerald-400 hover:text-emerald-300 font-semibold underline">
                Browse the grants catalog
              </Link>{" "}
              first &mdash; grants don't have to be repaid. Come back here if your project
              needs more capital than a grant can cover, or if you don't qualify for grant funding.
            </div>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
};

export default CapitalAccess;
